/**
 * One card per Azure subscription NOVA expects to see.
 *
 * The grid is built from the *expected* list, not from what Azure returned, so
 * a subscription NOVA cannot read still gets a card — an explicit "not
 * accessible" state with the reason on it. A subscription that is reachable but
 * genuinely empty (NO-SUB-BOI-TST holds nothing) reads as zero resources, which
 * is a different and equally honest answer.
 */

import { CircleCheck, CircleX, Icon } from "@norican/icons";
import { Badge, Card, Typography } from "@norican/react";

import type { CloudSubscription } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatCount } from "./status";

interface SubscriptionGridProps {
  subscriptions: CloudSubscription[];
}

export function SubscriptionGrid({ subscriptions }: SubscriptionGridProps) {
  const reachable = subscriptions.filter((subscription) => subscription.reachable).length;

  return (
    <PanelCard
      title="Subscriptions"
      caption={`${reachable} of ${subscriptions.length} readable by NOVA's credential`}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {subscriptions.map((subscription) => (
          <SubscriptionCard key={subscription.subscription_id} subscription={subscription} />
        ))}
      </div>
    </PanelCard>
  );
}

function SubscriptionCard({ subscription }: { subscription: CloudSubscription }) {
  const stopped = subscription.workloads_stopped;

  return (
    <Card
      variant="outline"
      accent="left"
      accentTone={!subscription.reachable ? "danger" : stopped > 0 ? "warning" : "neutral"}
    >
      <Card.Body padding="sm">
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <Typography variant="body-sm" as="h3">
              <span className="text-fg font-medium">{subscription.name}</span>
            </Typography>
            <Icon
              icon={subscription.reachable ? CircleCheck : CircleX}
              size="sm"
              tone={subscription.reachable ? "success" : "danger"}
            />
          </div>

          {subscription.reachable ? (
            <>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-fg text-2xl font-semibold tabular-nums">
                  {formatCount(subscription.resources)}
                </span>
                <span className="text-fg-muted text-xs">
                  resources · {formatCount(subscription.resource_groups)} groups
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone="neutral" variant="subtle" size="xs">
                  {subscription.workloads} workloads
                </Badge>
                {stopped > 0 && (
                  <Badge tone="warning" variant="subtle" size="xs">
                    {stopped} stopped
                  </Badge>
                )}
                {subscription.state && subscription.state !== "Enabled" && (
                  <Badge tone="danger" variant="subtle" size="xs">
                    {subscription.state}
                  </Badge>
                )}
              </div>
            </>
          ) : (
            <>
              <Badge tone="danger" variant="subtle" size="sm">
                Not accessible
              </Badge>
              <Typography variant="body-sm" tone="muted">
                {subscription.detail}
              </Typography>
            </>
          )}

          <Typography variant="micro" tone="muted" as="code" truncate>
            {subscription.subscription_id}
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}
